import { useState, useEffect, useCallback } from 'react';
import { 
  WifiOff, 
  CloudOff, 
  Database, 
  RefreshCw,
  Download,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  HardDrive
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import {
  fetchOfflineCache,
  fetchOfflineQueue,
  fetchOfflineStorage,
  syncOfflineQueue,
  clearOfflineCache,
  deleteOfflineCacheItem,
  type OfflineResource,
  type OfflineQueueItem,
} from '../services/sync-share-service';

interface CachedResource {
  id: string;
  url: string;
  size: string;
  cachedAt: Date;
  expiresAt: Date;
  type: 'page' | 'api' | 'asset';
}

function mapResource(r: OfflineResource): CachedResource {
  return {
    id: r.id,
    url: r.url,
    size: r.size,
    cachedAt: new Date(r.cached_at),
    expiresAt: new Date(r.expires_at),
    type: (r.type as CachedResource['type']) || 'page',
  };
}

export default function OfflineMode() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cachedResources, setCachedResources] = useState<CachedResource[]>([]);
  const [offlineQueue, setOfflineQueue] = useState<Array<{ id: string; action: string; timestamp: Date; status: string }>>([]);
  const [storageUsed, setStorageUsed] = useState('0 MB');
  const [storageLimit, setStorageLimit] = useState('50 MB');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cache, queue, storage] = await Promise.all([
        fetchOfflineCache(),
        fetchOfflineQueue(),
        fetchOfflineStorage(),
      ]);
      setCachedResources(cache.map(mapResource));
      setOfflineQueue(
        queue.map(item => ({
          id: item.id,
          action: `${item.type} ${item.entity}`,
          timestamp: new Date(item.created_at),
          status: 'pending',
        })),
      );
      setStorageUsed(storage.storage_used ?? '0 MB');
      setStorageLimit(storage.storage_limit ?? '50 MB');
    } catch (err) {
      console.error('Failed to load offline data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadData]);

  const clearCache = async (id?: string) => {
    try {
      if (id) {
        await deleteOfflineCacheItem(id);
      } else {
        await clearOfflineCache();
      }
      await loadData();
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setOfflineQueue(prev => prev.map(item => ({ ...item, status: 'syncing' })));
    try {
      await syncOfflineQueue();
      await loadData();
    } catch (err) {
      console.error('Offline sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const getStoragePercentage = () => {
    const used = parseFloat(storageUsed);
    const limit = parseFloat(storageLimit);
    if (!limit) return 0;
    return Math.min(100, (used / limit) * 100);
  };

  return (
    <div className="p-6 space-y-6 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-khiimori-text-primary">Offline Mode</h1>
          <p className="text-sm text-khiimori-text-muted mt-1">
            Manage cached data and offline capabilities
          </p>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
          isOnline 
            ? 'bg-success-500/10 text-success-400'
            : 'bg-error-500/10 text-error-400'
        }`}>
          {isOnline ? <WifiOff className="w-4 h-4" /> : <CloudOff className="w-4 h-4" />}
          <span className="text-sm font-medium">{isOnline ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      {loading && (
        <p className="text-sm text-khiimori-text-muted">Loading offline data...</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 glass">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-info-500/10 rounded-lg">
              <Database className="w-5 h-5 text-info-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Storage</h2>
              <p className="text-sm text-khiimori-text-muted">Local storage usage</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-khiimori-text-muted">Used Storage</span>
                <span className="text-lg font-bold text-khiimori-text-primary">
                  {storageUsed} / {storageLimit}
                </span>
              </div>
              <div className="w-full bg-khiimori-bg-elevated/5 rounded-full h-2">
                <div 
                  className="bg-primary-500 h-2 rounded-full transition-all"
                  style={{ width: `${getStoragePercentage()}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-khiimori-bg-elevated/5 rounded-lg text-center">
                <HardDrive className="w-6 h-6 text-khiimori-text-muted mx-auto mb-2" />
                <p className="text-2xl font-bold text-khiimori-text-primary">{cachedResources.length}</p>
                <p className="text-xs text-khiimori-text-muted">Cached Items</p>
              </div>
              <div className="p-4 bg-khiimori-bg-elevated/5 rounded-lg text-center">
                <Clock className="w-6 h-6 text-khiimori-text-muted mx-auto mb-2" />
                <p className="text-2xl font-bold text-khiimori-text-primary">{offlineQueue.length}</p>
                <p className="text-xs text-khiimori-text-muted">Pending Actions</p>
              </div>
            </div>

            <Button onClick={() => clearCache()} variant="danger">
              <Trash2 className="w-4 h-4" />
              Clear All Cache
            </Button>
          </div>
        </Card>

        <Card className="p-6 glass">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/10 rounded-lg">
                <RefreshCw className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h2 className="font-semibold text-khiimori-text-primary">Sync Queue</h2>
                <p className="text-sm text-khiimori-text-muted">Actions waiting to sync</p>
              </div>
            </div>
            {offlineQueue.length > 0 && (
              <Button onClick={syncNow} disabled={!isOnline || syncing} aria-label="Sync offline queue">
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Sync Now
              </Button>
            )}
          </div>
          
          <div className="space-y-3">
            {offlineQueue.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-khiimori-text-muted">
                <CheckCircle className="w-12 h-12 mb-2" />
                <p className="text-sm">All changes synced</p>
              </div>
            ) : (
              offlineQueue.map(item => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-khiimori-bg-elevated/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      item.status === 'syncing' ? 'bg-blue-500 animate-pulse' : 'bg-yellow-500'
                    }`} />
                    <div>
                      <p className="font-medium text-khiimori-text-primary">{item.action}</p>
                      <p className="text-xs text-khiimori-text-muted">
                        {item.timestamp.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    item.status === 'syncing' 
                      ? 'bg-info-500/10 text-info-400'
                      : 'bg-warning-500/10 text-warning-400'
                  }`}>
                    {item.status === 'syncing' ? 'Syncing...' : 'Pending'}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      <Card className="p-6 glass">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-500/10 rounded-lg">
              <Download className="w-5 h-5 text-success-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Cached Resources</h2>
              <p className="text-sm text-khiimori-text-muted">Data stored for offline access</p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-khiimori-border/10">
                <th className="text-left py-3 px-4 text-sm font-medium text-khiimori-text-muted">Resource</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-khiimori-text-muted">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-khiimori-text-muted">Size</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-khiimori-text-muted">Cached</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-khiimori-text-muted">Expires</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-khiimori-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cachedResources.map(resource => (
                <tr key={resource.id} className="border-b border-khiimori-border/10 hover:bg-khiimori-bg-elevated/5">
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-khiimori-text-primary">{resource.url}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      resource.type === 'page' 
                        ? 'bg-info-500/10 text-info-400'
                        : resource.type === 'api'
                        ? 'bg-primary-500/10 text-primary-400'
                        : 'bg-success-500/10 text-success-400'
                    }`}>
                      {resource.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-khiimori-text-muted">{resource.size}</td>
                  <td className="py-3 px-4 text-sm text-khiimori-text-muted">
                    {resource.cachedAt.toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      {resource.expiresAt < new Date() ? (
                        <span className="flex items-center gap-1 text-red-500">
                          <AlertCircle className="w-4 h-4" />
                          Expired
                        </span>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-khiimori-text-muted">
                            {resource.expiresAt.toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      onClick={() => clearCache(resource.id)}
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${resource.url} from cache`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 glass">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${isOnline ? 'bg-khiimori-bg-elevated/5' : 'bg-success-500/10'}`}>
              <CheckCircle className={`w-5 h-5 ${isOnline ? 'text-khiimori-text-muted' : 'text-success-400'}`} />
            </div>
            <h3 className="font-semibold text-khiimori-text-primary">View Cached Pages</h3>
          </div>
          <p className="text-sm text-khiimori-text-muted">
            Access previously viewed pages without internet connection
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {cachedResources.filter(r => r.type === 'page').slice(0, 3).map(r => (
              <span key={r.id} className="px-2 py-1 bg-khiimori-bg-elevated/5 text-khiimori-text-muted rounded text-xs">
                {r.url}
              </span>
            ))}
          </div>
        </Card>

        <Card className="p-6 glass">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${isOnline ? 'bg-khiimori-bg-elevated/5' : 'bg-success-500/10'}`}>
              <CheckCircle className={`w-5 h-5 ${isOnline ? 'text-khiimori-text-muted' : 'text-success-400'}`} />
            </div>
            <h3 className="font-semibold text-khiimori-text-primary">Queue Actions</h3>
          </div>
          <p className="text-sm text-khiimori-text-muted">
            Actions performed offline are queued and synced when online
          </p>
          <div className="mt-4">
            <span className="text-2xl font-bold text-khiimori-text-primary">{offlineQueue.length}</span>
            <span className="text-sm text-khiimori-text-muted ml-2">pending</span>
          </div>
        </Card>

        <Card className="p-6 glass">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-500/10 rounded-lg">
              <AlertCircle className="w-5 h-5 text-warning-400" />
            </div>
            <h3 className="font-semibold text-khiimori-text-primary">Sync Notifications</h3>
          </div>
          <p className="text-sm text-khiimori-text-muted">
            Get notified when offline actions are successfully synced
          </p>
          <div className="mt-4">
            <span className="text-green-500 flex items-center gap-1 text-sm">
              <CheckCircle className="w-4 h-4" /> Enabled
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
